import type { CloudVault } from '../vault/cloudVault';
import { isConflictSibling } from '../plan/board';

/**
 * Finding one entity by id, without reading the vault.
 *
 * The app writes `_catalog/YYYY-MM.jsonl` — one line per entity carrying its
 * path, id, kind and name. That is the whole lookup: a link says an id, and
 * the catalog says what it is and where it lives, at a cost of one small read
 * rather than opening thousands of notes.
 *
 * Ids are ULIDs, so an id carries the millisecond it was minted and therefore
 * names its own shard. Measured over 5,623 rows, 99% land in the month their
 * id predicts; every miss was a single month out at a boundary — a note
 * authored late in one month whose id was minted in the next. So the neighbours
 * are tried too, which turns "read 98 shards" into "read up to three".
 */
export interface CatalogEntry {
  path: string;
  id: string;
  kind: string;
  /** Arcs carry a name; a recording's name is its filename. */
  name: string | null;
}

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/** The millisecond a ULID was minted, or null if this is not one. */
export function ulidTime(id: string): number | null {
  if (id.length < 10) return null;
  let ms = 0;
  for (const ch of id.slice(0, 10).toUpperCase()) {
    const digit = CROCKFORD.indexOf(ch);
    if (digit < 0) return null;
    ms = ms * 32 + digit;
  }
  return ms;
}

/**
 * A fresh ULID: the millisecond it was minted, then 80 random bits. A file the
 * web writes declares one, and the importer takes it as the unit's own id.
 */
export function newUlid(
  nowMs: number = Date.now(),
  random: (bytes: number) => Uint8Array = (bytes) => crypto.getRandomValues(new Uint8Array(bytes)),
): string {
  let time = '';
  let ms = Math.floor(nowMs);
  for (let i = 0; i < 10; i++) {
    time = CROCKFORD.charAt(ms % 32) + time;
    ms = Math.floor(ms / 32);
  }
  // Five bits from each byte: 256 is a multiple of 32, so every symbol is equally likely.
  const bytes = random(16);
  let rand = '';
  for (let i = 0; i < 16; i++) rand += CROCKFORD.charAt((bytes[i] ?? 0) & 31);
  return time + rand;
}

function shardName(ms: number): string {
  const at = new Date(ms);
  return `${at.getUTCFullYear()}-${String(at.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** The shard the id names, then its neighbours — see the boundary skew above. */
export function shardsFor(id: string): string[] {
  const ms = ulidTime(id);
  if (ms === null) return [];
  const month = 30 * 24 * 60 * 60 * 1000;
  return [shardName(ms), shardName(ms - month), shardName(ms + month)];
}

function rowFor(text: string, id: string): CatalogEntry | null {
  // A substring test before parsing: a shard is thousands of lines and all but
  // one of them are someone else's.
  for (const line of text.split('\n')) {
    if (!line.includes(`"${id}"`)) continue;
    try {
      const row = JSON.parse(line) as { p?: string; id?: string; k?: string; n?: string };
      if (row.id !== id || !row.p) continue;
      return { path: row.p, id, kind: row.k ?? 'note', name: row.n ?? null };
    } catch {
      // A half-written line is not a reason to fail the lookup.
    }
  }
  return null;
}

/**
 * Where the thing with this id lives, or null if the catalog does not know it —
 * which is the honest answer for an id from another vault, a typo, or an entity
 * minted before the catalog existed.
 */
export async function resolveId(vault: Pick<CloudVault, 'readFile'>, id: string): Promise<CatalogEntry | null> {
  for (const shard of shardsFor(id)) {
    const text = await vault.readFile(`_catalog/${shard}.jsonl`);
    if (!text) continue;
    const found = rowFor(text, id);
    if (found) return found;
  }
  return null;
}

/**
 * What the catalog knows about one note, beyond where it lives: its kind
 * (`tombstone` marks a deleted/archived unit), its visibility word (`done`,
 * `archived`, `hidden-until <ms>` …, absent when Visible), and the tags it
 * carries — which is the only cheap answer to what a folder holds. A note's
 * tag edges otherwise live inside its structure manifest, one file per unit,
 * so asking without this means opening the vault.
 */
export interface CatalogNote {
  kind: string;
  v: string | null;
  /** The name the app holds for it, or null where nobody has named it. A file
   *  is named at its path when it is made and the path is never healed after,
   *  so this row is the only current record of a rename. */
  name: string | null;
  /** Tag NAMES. Present on 17,162 of 19,182 live rows. */
  tags: readonly string[];
  /** Tag IDS, in the same order. Written since 2026-08, so most rows predate
   *  it — see [tagKeysOf] for why both are kept. */
  tagIds: readonly string[];
  /** sha256 of the note file the row was written from — the same value the
   *  manifest carries for the path, so a match says the row still describes
   *  the file. Absent on a row that predates it. */
  hash?: string;
}

/**
 * Every catalogued note, keyed by path — the feed's join. One read per shard,
 * once per session; a note the catalog does not know is simply absent, which
 * downstream reads as visible — the honest default for anything catalogued
 * before the visibility word existed.
 */
export async function catalogByPath(
  vault: Pick<CloudVault, 'listPaths' | 'readFile'>,
): Promise<Map<string, CatalogNote>> {
  const out = new Map<string, CatalogNote>();
  // A conflict sibling is a stale COPY of a shard, and it sorts after the
  // shard it copies — so last-wins let an old row overwrite the live one: a
  // renamed workspace lost its name, and a visibility word or a tag edge can
  // go the same way. The plan load drops them for the same reason.
  const shards = vault
    .listPaths('_catalog')
    .filter((p) => p.endsWith('.jsonl') && !isConflictSibling(p));
  const texts = await Promise.all(shards.map((shard) => vault.readFile(shard)));
  for (const text of texts) {
    if (!text) continue;
    for (const line of text.split('\n')) {
      if (!line.trim()) continue;
      try {
        const row = JSON.parse(line) as {
          p?: string;
          k?: string;
          v?: string;
          n?: string;
          t?: string[];
          ti?: string[];
          h?: string;
        };
        if (row.p) {
          out.set(row.p, {
            kind: row.k ?? 'note',
            v: row.v ?? null,
            name: row.n ?? null,
            tags: row.t ?? [],
            tagIds: row.ti ?? [],
            ...(row.h ? { hash: row.h } : {}),
          });
        }
      } catch {
        // A half-written line costs one row, never the shard.
      }
    }
  }
  return out;
}

/**
 * The keys a row can be matched against a folder by — ids where it has them,
 * lower-cased names otherwise.
 *
 * Ids are exact and names are not: 12 of the 33 folders share a name with an
 * ordinary tag, so matching on the name alone puts that tag's notes in the
 * folder. Measured against the structure manifests, the twins are all but dead
 * — one unit each — except `Analytics`, where the folder holds 2 and its plain
 * twin holds 20. So a row's ids WIN when it has them and the names are the
 * fallback, which is exact for every row the app has re-exported since `ti`
 * landed and self-corrects for the rest as they are.
 */
export function tagKeysOf(note: CatalogNote): readonly string[] {
  return note.tagIds.length > 0 ? note.tagIds : note.tags.map((t) => t.toLowerCase());
}
